import dayjs from 'dayjs';
import { throttle } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { TabClickProps } from '@/components/atom/tab';
import { DoctorDetailLog } from '@/constants/logs/doctor';
import { EMedicalCategoryType } from '@/entities/medical-category';
import {
  DiagnosisPriceType,
  groupAndSortTreatmentsByIngredientAndDosage,
  mapTreatmentWithPriceType,
} from '@/entities/non-benefit';
import useGetDoctorIdByUserByTreatmentRequest from '@/hooks/diagnosis/useGetDoctorIdByUserByTreatmentRequest';
import useAddFamilyDoctor from '@/hooks/doctor/useAddFamilyDoctor';
import useGetDayOfWeek from '@/hooks/doctor/useGetDayOfWeek';
import useGetDoctorByUser from '@/hooks/doctor/useGetDoctorByUser';
import useRemoveFamilyDoctor from '@/hooks/doctor/useRemoveFamilyDoctor';
import useLoginViewModel from '@/hooks/login/useLoginViewModel';
import useLogger from '@/hooks/useLogger';
import DiagnosisCategoryService, {
  ECategoryType,
  EDeprecatedCategoryType,
} from '@/services/doctor/DiagnosisCategoryService';
import DoctorService from '@/services/doctor/DoctorService';
import theme from '@/styles/theme';
import { DiagnosisContactType, MedicineType, TelemedicineTargetType } from '@/types/bff/gql-schema';
import { EDiagnosisContactType } from '@/types/diagnosis';
import {
  BookingScheduleStatus,
  BookingScheduleTypeVo,
  Career,
  CareerType,
  DoctorBookingStatus,
  DoctorDetail,
  NonBenefitTreatment,
} from '@/types/doctor';
import { DoctorDetailViewModel as OriginDoctorDetailViewModel } from '@/views/doctor/detail/hooks/useDoctorDetailViewModel';
import { DoctorDetailTabType, calculateDoctorTabs } from '@/views/doctor/detail/lib';
import { useToast } from 'doctornow-design-system';

export interface SelectedDiagnosisSlot {
  startTimeSlotValue?: number;
  endTimeSlotValue?: number;
  targetDate?: string;
  bookingStatus?: BookingScheduleTypeVo;
}

export class NonBenefitTreatmentPriceCategory {
  readonly name: string;
  readonly id?: string;

  constructor(name: string, id?: string) {
    this.name = name;
    this.id = id;
  }
}

const DOCTOR_PROFILE_IMAGE_HEIGHT = 240;

interface DoctorDetailViewModel {
  selectedTab: OriginDoctorDetailViewModel['selectedTab'];
  doctorDetail: OriginDoctorDetailViewModel['doctorDetail'];
  treatmentRequestDoctor: OriginDoctorDetailViewModel['treatmentRequestDoctor'];
  isOperationTimeTableOpened: OriginDoctorDetailViewModel['isOperationTimeTableOpened'];
  isPointPriceVisible: OriginDoctorDetailViewModel['isPointPriceVisible'];
  isLoggedIn: OriginDoctorDetailViewModel['isLoggedIn'];
  isHeaderTransparent: OriginDoctorDetailViewModel['isHeaderTransparent'];
  isNonBenefitCategory: OriginDoctorDetailViewModel['isNonBenefitCategory'];
  isVisibleTodayDiagnosisSlotSelectBox: OriginDoctorDetailViewModel['isVisibleTodayDiagnosisSlotSelectBox'];
  isDoctorDetailLoading: OriginDoctorDetailViewModel['isDoctorDetailLoading'];
  isGoBackConfirmAlertOpened: OriginDoctorDetailViewModel['isGoBackConfirmAlertOpened'];
  isWithDispensation: OriginDoctorDetailViewModel['isWithDispensation'];
  diagnosisContactType: OriginDoctorDetailViewModel['diagnosisContactType'];
  contactType: OriginDoctorDetailViewModel['contactType'];
  isFamilyDoctor: OriginDoctorDetailViewModel['isFamilyDoctor'];
  handleUpdateFamilyDoctor: OriginDoctorDetailViewModel['handleUpdateFamilyDoctor'];
  categoryId: OriginDoctorDetailViewModel['categoryId'];
  diagnosisType: OriginDoctorDetailViewModel['diagnosisType'];
  doctorPublicId: OriginDoctorDetailViewModel['doctorPublicId'];
  getDoctorTreatments: OriginDoctorDetailViewModel['getDoctorTreatments'];
  shouldShowTreatmentSection: OriginDoctorDetailViewModel['shouldShowTreatmentSection'];
  isNonF2FDiagnosis: OriginDoctorDetailViewModel['isNonF2FDiagnosis'];
  onClickTab: OriginDoctorDetailViewModel['onClickTab'];
  getTabName: OriginDoctorDetailViewModel['getTabName'];
  getTabs: OriginDoctorDetailViewModel['getTabs'];
  goToReview: OriginDoctorDetailViewModel['goToReview'];
  toggleOperationTimeTable: OriginDoctorDetailViewModel['toggleOperationTimeTable'];
  getCareerDisplayText: OriginDoctorDetailViewModel['getCareerDisplayText'];
  getNonBenefitPriceCategories: OriginDoctorDetailViewModel['getNonBenefitPriceCategories'];
  isCategorySelected: OriginDoctorDetailViewModel['isCategorySelected'];
  selectNonBenefitPriceCategory: OriginDoctorDetailViewModel['selectNonBenefitPriceCategory'];
  getNonBenefitTreatmentPriceList: OriginDoctorDetailViewModel['getNonBenefitTreatmentPriceList'];
  togglePointPrice: OriginDoctorDetailViewModel['togglePointPrice'];
  getNonBenefitTreatmentPriceListByCategory: OriginDoctorDetailViewModel['getNonBenefitTreatmentPriceListByCategory'];
  isDietCategoryFor: OriginDoctorDetailViewModel['isDietCategoryFor'];
  toggleWithDispensation: OriginDoctorDetailViewModel['toggleWithDispensation'];
  getDoctorDisplayName: OriginDoctorDetailViewModel['getDoctorDisplayName'];
  getReviewStatisticGroupsForSummary: OriginDoctorDetailViewModel['getReviewStatisticGroupsForSummary'];
  getDisplayAverageRating: OriginDoctorDetailViewModel['getDisplayAverageRating'];
  getDisplayReviewCount: OriginDoctorDetailViewModel['getDisplayReviewCount'];
  getDoctorDuties: OriginDoctorDetailViewModel['getDoctorDuties'];
  hasNoCareer: OriginDoctorDetailViewModel['hasNoCareer'];
  hasNoBadge: OriginDoctorDetailViewModel['hasNoBadge'];
  hasNoNotice: OriginDoctorDetailViewModel['hasNoNotice'];
  getIsBookingAvailable: OriginDoctorDetailViewModel['getIsBookingAvailable'];
  getDoctorBookingStatus: OriginDoctorDetailViewModel['getDoctorBookingStatus'];
  getDoctorBookingStatusText: OriginDoctorDetailViewModel['getDoctorBookingStatusText'];
  callHospital: OriginDoctorDetailViewModel['callHospital'];
  getHospitalAddress: OriginDoctorDetailViewModel['getHospitalAddress'];
  shouldShowPointTermInfo: OriginDoctorDetailViewModel['shouldShowPointTermInfo'];
  shouldShowNonBenefitCategoryHeader: OriginDoctorDetailViewModel['shouldShowNonBenefitCategoryHeader'];
  isF2FDiagnosis: OriginDoctorDetailViewModel['isF2FDiagnosis'];
  openGoBackConfirmAlert: OriginDoctorDetailViewModel['openGoBackConfirmAlert'];
  closeGoBackConfirmAlert: OriginDoctorDetailViewModel['closeGoBackConfirmAlert'];
  getBenefitPriceSectionTitle: OriginDoctorDetailViewModel['getBenefitPriceSectionTitle'];
}

export interface LoadedDoctorDetailViewModel extends DoctorDetailViewModel {
  doctorDetail: DoctorDetail;
}

export interface WebTelemedicineDoctorDetailPageQueryParam {
  doctorId?: string;
  categoryId?: string;
  type?: EMedicalCategoryType;
  telemedicineTargetType?: TelemedicineTargetType;
  treatmentMedicineId?: string;
  treatmentMedicineUnitId?: string;
  treatmentMedicineStrengthId?: string;
  contactType?: EDiagnosisContactType;
  priceType?: DiagnosisPriceType;
  entry?: EDeprecatedCategoryType;
  isNeedReview?: boolean;
  vaccinationTypeId?: string;
  vaccinationUnitId?: string;
  vaccinationFreePolicyId?: string;
}

const parseMedicalCategoryCategoryTypeOf = (category?: EMedicalCategoryType): ECategoryType => {
  switch (category) {
    case EMedicalCategoryType.TREATMENT:
      return ECategoryType.Treatment;
    case EMedicalCategoryType.DEPARTMENT:
    default:
      return ECategoryType.Department;
  }
};

const useDoctorDetailViewModel = (): DoctorDetailViewModel => {
  const router = useRouter();
  const {
    doctorId: doctorPublicId,
    categoryId,
    type,
    telemedicineTargetType,
    contactType = EDiagnosisContactType.NonF2F,
    priceType = DiagnosisPriceType.DiagnosisOnly,
    isNeedReview,
  } = router.query as WebTelemedicineDoctorDetailPageQueryParam;

  const isNonBenefitCategory = DiagnosisCategoryService.isNonBenefitCategory(
    parseMedicalCategoryCategoryTypeOf(type),
    categoryId
  );
  const isDietCategory = DiagnosisCategoryService.isDietCategory(categoryId);

  const loginViewModel = useLoginViewModel();
  const logger = useLogger();
  const { dayOfWeek } = useGetDayOfWeek(dayjs().format('YYYY-MM-DD'));
  const { doctorDetail, isDoctorDetailLoading, refetchDoctorByUser } = useGetDoctorByUser({
    doctorId: doctorPublicId ?? '',
    telemedicineEligibleTargetType: telemedicineTargetType,
    dayOfWeek: dayOfWeek ?? undefined,
    contactType: contactType ?? undefined,
  });

  const { doctor: treatmentRequestDoctor } = useGetDoctorIdByUserByTreatmentRequest(
    doctorDetail?.doctor.id ?? '',
    contactType,
    !isDoctorDetailLoading && !categoryId
  );

  const openGoBackConfirmAlert = () => {
    setIsGoBackConfirmAlertOpened(true);
  };

  const closeGoBackConfirmAlert = () => {
    setIsGoBackConfirmAlertOpened(false);
  };

  const { handleAddFamilyDoctor, setDoctorDetailCacheDataAfterAddFamilyDoctor } = useAddFamilyDoctor();
  const { handleRemoveFamilyDoctors, setDoctorDetailCacheDataAfterRemoveFamilyDoctor } = useRemoveFamilyDoctor();

  const { toast } = useToast();

  const addFamilyDoctor = async (doctorId: string) => {
    try {
      const response = await handleAddFamilyDoctor(doctorId);
      toast({ text: '단골 의사로 등록했어요.\n더보기 메뉴에서 확인할 수 있어요.' });
      logger.log({
        eventName: DoctorDetailLog.add_family_doctor,
        payload: {
          doctorId,
          doctorPublicId: doctorPublicId ?? '',
          telemedicineTargetType: telemedicineTargetType ?? '',
          contactType: contactType ?? '',
        },
      });
      setDoctorDetailCacheDataAfterAddFamilyDoctor({
        doctorPublicId: doctorPublicId ?? '',
        telemedicineEligibleTargetType: telemedicineTargetType ?? TelemedicineTargetType.AlwaysEligible,
        contactType: contactType === EDiagnosisContactType.F2F ? DiagnosisContactType.F2F : DiagnosisContactType.NonF2F,
        isSelectedVaccination: false,
        isAdded: response,
      });
    } catch (error) {
      if (error instanceof Error) {
        toast({ text: error.message });
        refetchDoctorByUser();
      }
    }
  };

  const removeFamilyDoctors = async (doctorIds: string[]) => {
    try {
      const response = await handleRemoveFamilyDoctors(doctorIds);
      toast({ text: '단골 의사에서 제외했어요.' });
      logger.log({
        eventName: DoctorDetailLog.remove_family_doctor,
        payload: {
          doctorIds: doctorIds.join(','),
          doctorPublicId: doctorPublicId ?? '',
          telemedicineTargetType: telemedicineTargetType ?? '',
          contactType: contactType ?? '',
        },
      });
      setDoctorDetailCacheDataAfterRemoveFamilyDoctor({
        doctorPublicId: doctorPublicId ?? '',
        telemedicineEligibleTargetType: telemedicineTargetType ?? TelemedicineTargetType.AlwaysEligible,
        contactType: contactType === EDiagnosisContactType.F2F ? DiagnosisContactType.F2F : DiagnosisContactType.NonF2F,
        isSelectedVaccination: false,
        isRemoved: response,
      });
    } catch (error) {
      if (error instanceof Error) {
        toast({ text: error.message });
        refetchDoctorByUser();
      }
    }
  };

  const isFamilyDoctor = !!doctorDetail?.familyDoctorInfo;

  const handleUpdateFamilyDoctor = throttle(async (doctorIds: string[]) => {
    if (isFamilyDoctor) {
      await removeFamilyDoctors(doctorIds);
      return;
    }

    const doctorId = doctorIds.pop();

    if (!doctorId) {
      return;
    }

    await addFamilyDoctor(doctorId);
  }, 500);

  const [isHeaderTransparent, setIsHeaderTransparent] = useState<boolean>(true);

  const [isOperationTimeTableOpened, setIsOperationTimeTableOpened] = useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<DoctorDetailTabType>(DoctorDetailTabType.NonBenefitPrice);

  const [selectedNonBenefitTreatmentPriceCategory, setSelectedNonBenefitTreatmentCategory] =
    useState<NonBenefitTreatmentPriceCategory>();

  const [isPointPriceVisible, setIsPointPriceVisible] = useState<boolean>(true);
  const [isGoBackConfirmAlertOpened, setIsGoBackConfirmAlertOpened] = useState<boolean>(false);

  const [isWithDispensation, setIsWithDispensation] = useState<boolean>(
    priceType === DiagnosisPriceType.WithDispensation
  );

  const diagnosisContactType = contactType === EDiagnosisContactType.F2F ? '대면' : '비대면';

  const toggleWithDispensation = () => {
    setIsWithDispensation(!isWithDispensation);
  };

  const toggleOperationTimeTable = () => {
    setIsOperationTimeTableOpened(!isOperationTimeTableOpened);
  };

  const getIsBookingAvailable = () => {
    if (!doctorDetail?.diagnosisBookingSchedules?.length) {
      return false;
    }

    return DoctorService.getIsBookingAvailable(
      doctorDetail?.diagnosisBookingSchedules,
      isNonF2FDiagnosis(),
      doctorDetail.doctor.isF2FBookable,
      doctorDetail.doctor.isNonF2FBookable,
      doctorDetail.doctor.isNonF2FNonBenefitBookable
    );
  };

  const onClickTab = (props: TabClickProps<DoctorDetailTabType>) => {
    setSelectedTab(props.value);
    router.replace(`#${props.value}`, undefined, { scroll: false });
  };

  const getTabName = (tabType: DoctorDetailTabType, fallbackLabel: string): string => {
    const tabNameMap: Record<DoctorDetailTabType, string> = {
      [DoctorDetailTabType.DoctorProfile]: '의사 정보',
      [DoctorDetailTabType.NonBenefitPrice]: '진료비',
      [DoctorDetailTabType.BenefitPrice]: '진료비',
      [DoctorDetailTabType.Review]: '진료 후기',
      [DoctorDetailTabType.Question]: '질문',
      [DoctorDetailTabType.VaccinationPrice]: '진료비',
    };

    return tabNameMap[tabType] ?? fallbackLabel;
  };

  const goToReview = () => {
    onClickTab({
      label: '진료 후기',
      value: DoctorDetailTabType.Review,
    });
  };

  const getCareerDisplayText = (career: Career): string => {
    switch (career.type) {
      case CareerType.Current:
        return `현 ${career.content}`;
      case CareerType.Completion:
        return `${career.content} 수료`;
      case CareerType.Past:
        return `전 ${career.content}`;
      case CareerType.Graduation:
        return `${career.content} 졸업`;
    }
  };

  const getNonBenefitPriceCategories = () => {
    if (!doctorDetail || !doctorDetail.nonBenefitTreatments?.length) {
      return [];
    }
    const categories = [...doctorDetail.nonBenefitTreatments];

    // 대면진료 + 다이어트 카테고리로 진입한 경우, 다이어트 카테고리만 노출
    if (isF2FDiagnosis() && isDietCategory) {
      const dietOnly = categories.filter((category) => DiagnosisCategoryService.isDietCategory(category.id));
      return [...dietOnly.map((treatment) => new NonBenefitTreatmentPriceCategory(treatment.name, treatment.id))];
    }

    if (categoryId) {
      categories.sort((category) => (category.id === categoryId ? -1 : 1));
    }

    return [...categories.map((treatment) => new NonBenefitTreatmentPriceCategory(treatment.name, treatment.id))];
  };

  const selectNonBenefitPriceCategory = (category: NonBenefitTreatmentPriceCategory) => {
    setSelectedNonBenefitTreatmentCategory(category);
  };

  const isCategorySelected = (category: NonBenefitTreatmentPriceCategory) => {
    return Boolean(selectedNonBenefitTreatmentPriceCategory?.id === category.id);
  };

  const getNonBenefitTreatmentPriceList = () => {
    if (!doctorDetail || !doctorDetail.nonBenefitTreatments?.length) {
      return [];
    }
    const treatments: NonBenefitTreatment[] =
      doctorDetail.nonBenefitTreatments
        ?.filter((treatment) => treatment.id === selectedNonBenefitTreatmentPriceCategory?.id)
        .map((treatment) => mapTreatmentWithPriceType(treatment, isWithDispensation)) || [];

    return groupAndSortTreatmentsByIngredientAndDosage(treatments);
  };

  const getNonBenefitTreatmentPriceListByCategory = (
    category: NonBenefitTreatmentPriceCategory
  ): Array<NonBenefitTreatment> => {
    if (!doctorDetail || !doctorDetail.nonBenefitTreatments?.length) {
      return [];
    }

    let treatments: NonBenefitTreatment[];

    if (!category) {
      treatments = doctorDetail.nonBenefitTreatments.map((treatment) =>
        mapTreatmentWithPriceType(treatment, isWithDispensation)
      );
    } else {
      treatments =
        doctorDetail.nonBenefitTreatments
          ?.filter((treatment) => treatment.id === category.id)
          .map((treatment) => mapTreatmentWithPriceType(treatment, isWithDispensation)) || [];
    }

    return groupAndSortTreatmentsByIngredientAndDosage(treatments);
  };

  const isDietCategoryFor = (category: NonBenefitTreatmentPriceCategory): boolean => {
    if (!category?.id) return false;
    return isDietCategory;
  };

  const togglePointPrice = () => {
    setIsPointPriceVisible(!isPointPriceVisible);
  };

  const getDoctorDisplayName = (name: string, medicineType: MedicineType) => {
    if (medicineType === MedicineType.OrientalDoctor) {
      return `${name} 한의사`;
    }
    return `${name} 의사`;
  };

  const getReviewStatisticGroupsForSummary = () => {
    if (!doctorDetail?.doctorDiagnosisTypeReviewRatingStatistics) {
      return [];
    }
    const sortedReviewsStatistics = [...doctorDetail.doctorDiagnosisTypeReviewRatingStatistics].sort((prev, next) => {
      if (prev.diagnosisTypeId === categoryId && prev.diagnosisType === type?.toUpperCase()) {
        return -1;
      }

      if (Number(prev.averageRating) < Number(next.averageRating)) {
        return 1;
      }

      if (Number(prev.averageRating) > Number(next.averageRating)) {
        return -1;
      }

      return 0;
    });

    return sortedReviewsStatistics.slice(0, 3);
  };

  const getDisplayAverageRating = () => {
    if (!doctorDetail) {
      return '0';
    }
    return `${doctorDetail.doctor.averageRating.toFixed(1)}`;
  };

  const stringifyReviewCount = (count: number) => {
    if (count <= 100) {
      return `${count}`;
    }
    return `${Math.floor(count / 100) * 100}+`;
  };

  const getDisplayReviewCount = () => {
    if (!doctorDetail) {
      return '0';
    }

    return stringifyReviewCount(doctorDetail.doctor.reviewCount);
  };

  const hasNoCareer = () => {
    if (!doctorDetail) {
      return true;
    }
    return doctorDetail.doctor.career.length === 0;
  };

  const hasNoBadge = () => {
    if (!doctorDetail) {
      return true;
    }
    return doctorDetail.badges.length === 0;
  };

  const hasNoNotice = () => {
    if (!doctorDetail) {
      return true;
    }
    return !doctorDetail.notice.title || !doctorDetail.notice.content;
  };

  const getCalculatedTabs = () => {
    return calculateDoctorTabs({
      type: parseMedicalCategoryCategoryTypeOf(type),
      categoryId,
      hasNonBenefitPriceCategories: getNonBenefitPriceCategories().length > 0,
      isNonBenefitCategory,
      isWebTelemedicineFlow: true,
      isNonF2FDiagnosis: isNonF2FDiagnosis(),
      isDietCategory,
    });
  };

  const getTabs = () => {
    if (!doctorDetail) {
      return [];
    }

    return getCalculatedTabs();
  };

  const getDoctorBookingStatus = () => {
    if (!doctorDetail?.diagnosisBookingSchedules.length) {
      return DoctorBookingStatus.BookingUnavailable;
    }

    const today = dayjs();
    const todaySchedule = doctorDetail?.diagnosisBookingSchedules.find((schedule) =>
      today.isSame(schedule.targetDate, 'date')
    );

    if (todaySchedule?.status === BookingScheduleStatus.Bookable) {
      return DoctorBookingStatus.TodayBookable;
    }
    const tomorrow = dayjs().add(1, 'day');
    const tomorrowSchedule = doctorDetail?.diagnosisBookingSchedules.find((schedule) =>
      tomorrow.isSame(schedule.targetDate, 'date')
    );

    if (tomorrowSchedule?.status === BookingScheduleStatus.Bookable) {
      return DoctorBookingStatus.TomorrowBookable;
    }

    return DoctorBookingStatus.BookingUnavailable;
  };

  const getDoctorBookingStatusText = (status: DoctorBookingStatus) => {
    switch (status) {
      case DoctorBookingStatus.BookingUnavailable:
        return '예약 마감';
      case DoctorBookingStatus.TodayBookable:
        return '오늘 예약';
      case DoctorBookingStatus.TomorrowBookable:
        return '내일 예약';
    }
  };

  const getHospitalAddress = () => {
    if (!doctorDetail) {
      return '';
    }

    if (doctorDetail.hospital?.detailAddress) {
      return `${doctorDetail.hospital.roadAddress}, ${doctorDetail.hospital.detailAddress}`;
    } else {
      return doctorDetail.hospital?.roadAddress || '';
    }
  };

  const getDoctorTreatments = () => {
    if (!doctorDetail) {
      return [];
    }

    const treatments = [...doctorDetail.treatments];

    if (type == EMedicalCategoryType.TREATMENT) {
      treatments.sort((treatment) => (treatment.treatmentId === categoryId ? -1 : 1));
    }

    return treatments;
  };

  const getDoctorDuties = () => {
    if (!doctorDetail) {
      return [];
    }

    if (contactType === EDiagnosisContactType.NonF2F) {
      return doctorDetail.doctor.doctorDuties.nonF2f;
    }

    if (contactType === EDiagnosisContactType.F2F) {
      return doctorDetail.doctor.doctorDuties.f2f;
    }

    return [];
  };

  const callHospital = () => {
    if (!doctorDetail || !doctorDetail.hospital.tel) {
      return;
    }

    window.location.href = `tel:${doctorDetail.hospital.tel}`;
  };

  const isNonF2FDiagnosis = () => contactType === EDiagnosisContactType.NonF2F;
  const isF2FDiagnosis = () => contactType === EDiagnosisContactType.F2F;

  const shouldShowPointTermInfo = () => getNonBenefitPriceCategories().length !== 0;

  const shouldShowNonBenefitCategoryHeader = () => {
    if (isF2FDiagnosis()) {
      return false;
    }

    return getNonBenefitPriceCategories().length !== 2; // [전체, 과목] 이렇게 있는경우가 있어 2로 지정
  };

  const isVisibleTodayDiagnosisSlotSelectBox = useMemo(() => {
    if (!doctorDetail?.diagnosisBookingSchedules) return;

    // 예약 받기 off
    if (!doctorDetail?.diagnosisBookingSchedules?.length) return;

    const isEnabledDiagnosisRequest = doctorDetail?.diagnosisBookingSchedules?.some(
      (schedule) => schedule.status === BookingScheduleStatus.Bookable
    );
    return isEnabledDiagnosisRequest;
  }, [doctorDetail?.diagnosisBookingSchedules]);

  const shouldShowTreatmentSection = () => {
    if (!doctorDetail) {
      return false;
    }

    return doctorDetail.treatments.length > 0;
  };

  const getBenefitPriceSectionTitle = () => {
    if (type === EMedicalCategoryType.DEPARTMENT) {
      const department = doctorDetail?.doctor.departments.find((dept) => dept.id === categoryId);
      return department?.name || '';
    }

    const treatment = doctorDetail?.treatments.find((treat) => treat.treatmentId === categoryId);
    return treatment?.treatmentName || '';
  };

  useEffect(() => {
    if (isDoctorDetailLoading) {
      return;
    }

    if (!window.location.hash) {
      return;
    }

    if (document.readyState !== 'complete') {
      return;
    }

    const elementId = window.location.hash.substring(1);
    const element = document.getElementById(elementId);

    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200); // 시간에 따라 탭 이동이 안되는 경우가 있어서 200ms로 변경 (refactoring)
    }
  }, [isDoctorDetailLoading]);

  useEffect(() => {
    if (!router) {
      return;
    }

    if (typeof window === undefined) {
      return;
    }

    if (!window.location.hash) {
      return;
    }

    const tabType = window.location.hash.substring(1) as DoctorDetailTabType;
    setSelectedTab(tabType);
  }, [router]);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY >= DOCTOR_PROFILE_IMAGE_HEIGHT - theme.size.mobileWebHeader.height) {
        return setIsHeaderTransparent(false);
      }
      return setIsHeaderTransparent(true);
    };

    document.getElementsByTagName('html')[0].style.scrollBehavior = 'smooth';
    window.addEventListener('scroll', onScroll);
    return () => {
      document.getElementsByTagName('html')[0].style.scrollBehavior = '';
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    const tabs = getCalculatedTabs();
    setSelectedTab(tabs[0].value);
  }, [doctorDetail]);

  useEffect(() => {
    if (isDoctorDetailLoading) {
      return;
    }

    if (isNeedReview) {
      goToReview();
    }
  }, [isNeedReview, isDoctorDetailLoading]);

  return {
    selectedTab,
    doctorDetail,
    treatmentRequestDoctor,
    isOperationTimeTableOpened,
    isPointPriceVisible,
    isLoggedIn: loginViewModel.isLoggedIn,
    isHeaderTransparent,
    isNonBenefitCategory,
    isVisibleTodayDiagnosisSlotSelectBox,
    isDoctorDetailLoading,
    isGoBackConfirmAlertOpened,
    isWithDispensation,
    diagnosisContactType,
    contactType,
    isFamilyDoctor,
    handleUpdateFamilyDoctor,
    categoryId,
    diagnosisType: type,
    doctorPublicId,
    getDoctorTreatments,
    shouldShowTreatmentSection,
    isNonF2FDiagnosis,
    onClickTab,
    getTabName,
    getTabs,
    goToReview,
    toggleOperationTimeTable,
    getCareerDisplayText,
    getNonBenefitPriceCategories,
    isCategorySelected,
    selectNonBenefitPriceCategory,
    getNonBenefitTreatmentPriceList,
    togglePointPrice,
    getNonBenefitTreatmentPriceListByCategory,
    isDietCategoryFor,
    toggleWithDispensation,
    getDoctorDisplayName,
    getReviewStatisticGroupsForSummary,
    getDisplayAverageRating,
    getDisplayReviewCount,
    getDoctorDuties,
    hasNoCareer,
    hasNoBadge,
    hasNoNotice,
    getIsBookingAvailable,
    getDoctorBookingStatus,
    getDoctorBookingStatusText,
    callHospital,
    getHospitalAddress,
    shouldShowPointTermInfo,
    shouldShowNonBenefitCategoryHeader,
    isF2FDiagnosis,
    openGoBackConfirmAlert,
    closeGoBackConfirmAlert,
    getBenefitPriceSectionTitle,
  };
};

export default useDoctorDetailViewModel;
